import React, { createContext, useState, useEffect, useContext } from 'react';

export type ChordNotationType = 'full' | 'symbols';

type SettingsContextType = {
  isDarkMode: boolean;
  chordNotationType: ChordNotationType;
  isAutoLevelUpEnabled: boolean;
  isSoundEnabled: boolean;
  toggleDarkMode: () => void;
  setChordNotationType: (type: ChordNotationType) => void;
  setAutoLevelUpEnabled: (enabled: boolean) => void;
  setSoundEnabled: (enabled: boolean) => void;
};

const SettingsContext = createContext<SettingsContextType>({
  isDarkMode: false,
  chordNotationType: 'full',
  isAutoLevelUpEnabled: true,
  isSoundEnabled: true,
  toggleDarkMode: () => {},
  setChordNotationType: () => {},
  setAutoLevelUpEnabled: () => {},
  setSoundEnabled: () => {},
});

export const SettingsProvider: React.FC<{children: React.ReactNode}> = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme === 'dark' ||
      (!savedTheme && window.matchMedia('(prefers-color-scheme: dark)').matches);
  });

  const [chordNotationType, setChordNotationTypeState] = useState<ChordNotationType>(() => {
    const savedChordNotationType = localStorage.getItem('chordNotationType') as ChordNotationType;
    return savedChordNotationType || 'full';
  });

  const [isAutoLevelUpEnabled, setIsAutoLevelUpEnabledState] = useState(() => {
    const savedAutoLevelUp = localStorage.getItem('isAutoLevelUpEnabled');
    return savedAutoLevelUp === null ? true : savedAutoLevelUp === 'true';
  });

  const [isSoundEnabled, setIsSoundEnabledState] = useState(() => {
    const savedSoundEnabled = localStorage.getItem('isSoundEnabled');
    return savedSoundEnabled === null ? true : savedSoundEnabled === 'true';
  });

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDarkMode]);

  useEffect(() => {
    localStorage.setItem('chordNotationType', chordNotationType);
  }, [chordNotationType]);

  useEffect(() => {
    localStorage.setItem('isAutoLevelUpEnabled', isAutoLevelUpEnabled.toString());
  }, [isAutoLevelUpEnabled]);

  useEffect(() => {
    localStorage.setItem('isSoundEnabled', isSoundEnabled.toString());
  }, [isSoundEnabled]);

  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
  };

  const setChordNotationType = (type: ChordNotationType) => {
    setChordNotationTypeState(type);
  };

  const setAutoLevelUpEnabled = (enabled: boolean) => {
    setIsAutoLevelUpEnabledState(enabled);
  };

  const setSoundEnabled = (enabled: boolean) => {
    setIsSoundEnabledState(enabled);
  };

  return (
    <SettingsContext.Provider value={{
      isDarkMode,
      chordNotationType,
      isAutoLevelUpEnabled,
      isSoundEnabled,
      toggleDarkMode,
      setChordNotationType,
      setAutoLevelUpEnabled,
      setSoundEnabled
    }}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => useContext(SettingsContext);
